import {
  cellInBounds,
  gridCellToWorld,
  gridMeta,
  worldToGridCell,
  type GridSector,
} from './floor-grid.js';

export type FloorSector = 'salon' | 'patio' | 'storage';

export type ModuleKind = 'duo' | 'seis' | 'modulo';

export interface PhysicalModule {
  moduleId: string;
  logicalTableId: number;
  kind: ModuleKind;
  seatCapacity: number;
  sector: FloorSector;
  gridCol: number;
  gridRow: number;
  gridCols: number;
  gridRows: number;
  placed: boolean;
  occupied: boolean;
  sessionId: string | null;
}

export interface TableSession {
  sessionId: string;
  logicalTableId: number;
  guests: number;
  status: 'open' | 'closing';
}

export interface LogicalTable {
  id: number;
  baseSeats: number;
  maxSeats: number;
  sector: FloorSector;
  flexible: boolean;
  session: TableSession | null;
}

export interface FloorLayoutModule extends PhysicalModule {
  worldX: number;
  worldZ: number;
}

export interface FloorState {
  tables: LogicalTable[];
  modules: PhysicalModule[];
  message: string;
  atCapacity: boolean;
}

function gridToWorld(sector: Exclude<FloorSector, 'storage'>, col: number, row: number, cols: number, rows: number) {
  return gridCellToWorld(sector as GridSector, col, row, cols, rows);
}

function footprintForGuests(guests: number): { cols: number; rows: number } {
  if (guests <= 2) return { cols: 1, rows: 1 };
  if (guests <= 4) return { cols: 1, rows: 1 };
  if (guests <= 6) return { cols: 2, rows: 1 };
  if (guests <= 10) return { cols: 2, rows: 2 };
  if (guests <= 18) return { cols: 3, rows: 2 };
  return { cols: 4, rows: 2 };
}

function collides(
  a: { col: number; row: number; cols: number; rows: number },
  b: { col: number; row: number; cols: number; rows: number },
): boolean {
  return a.col < b.col + b.cols && a.col + a.cols > b.col && a.row < b.row + b.rows && a.row + a.rows > b.row;
}

function occupiedCells(modules: PhysicalModule[], sector: FloorSector): PhysicalModule[] {
  return modules.filter((m) => m.placed && m.sector === sector);
}

function findSlot(
  sector: Exclude<FloorSector, 'storage'>,
  footprint: { cols: number; rows: number },
  modules: PhysicalModule[],
): { col: number; row: number } | null {
  const grid = gridMeta(sector as GridSector);
  const placed = occupiedCells(modules, sector);
  for (let row = 0; row <= grid.rows - footprint.rows; row += 1) {
    for (let col = 0; col <= grid.cols - footprint.cols; col += 1) {
      const candidate = { col, row, cols: footprint.cols, rows: footprint.rows };
      const hit = placed.some((m) =>
        collides(candidate, { col: m.gridCol, row: m.gridRow, cols: m.gridCols, rows: m.gridRows }),
      );
      if (!hit) return { col, row };
    }
  }
  return null;
}

function createLogicalTables(): LogicalTable[] {
  const tables: LogicalTable[] = [];
  for (let id = 1; id <= 20; id += 1) {
    if (id <= 5) {
      tables.push({ id, baseSeats: 2, maxSeats: 4, sector: 'salon', flexible: false, session: null });
    } else if (id <= 10) {
      tables.push({ id, baseSeats: 6, maxSeats: 8, sector: 'salon', flexible: false, session: null });
    } else {
      tables.push({ id, baseSeats: 4, maxSeats: 30, sector: 'patio', flexible: true, session: null });
    }
  }
  return tables;
}

function createModules(): PhysicalModule[] {
  const modules: PhysicalModule[] = [];
  for (let id = 1; id <= 5; id += 1) {
    modules.push({
      moduleId: `m-${id}-a`,
      logicalTableId: id,
      kind: 'duo',
      seatCapacity: 2,
      sector: 'salon',
      gridCol: id - 1,
      gridRow: 1,
      gridCols: 1,
      gridRows: 1,
      placed: true,
      occupied: false,
      sessionId: null,
    });
  }
  for (let id = 6; id <= 10; id += 1) {
    modules.push({
      moduleId: `m-${id}-a`,
      logicalTableId: id,
      kind: 'seis',
      seatCapacity: 6,
      sector: 'salon',
      gridCol: (id - 6) % 3,
      gridRow: 3 + Math.floor((id - 6) / 3),
      gridCols: 2,
      gridRows: 1,
      placed: true,
      occupied: false,
      sessionId: null,
    });
  }
  for (let id = 11; id <= 20; id += 1) {
    modules.push({
      moduleId: `m-${id}-a`,
      logicalTableId: id,
      kind: 'modulo',
      seatCapacity: 4,
      sector: 'storage',
      gridCol: 0,
      gridRow: 0,
      gridCols: 1,
      gridRows: 1,
      placed: false,
      occupied: false,
      sessionId: null,
    });
    modules.push({
      moduleId: `m-${id}-b`,
      logicalTableId: id,
      kind: 'modulo',
      seatCapacity: 4,
      sector: 'storage',
      gridCol: 0,
      gridRow: 0,
      gridCols: 1,
      gridRows: 1,
      placed: false,
      occupied: false,
      sessionId: null,
    });
  }
  return modules;
}

export class DynamicFloorEngine {
  private tables = createLogicalTables();
  private modules = createModules();
  private message = 'Restaurante preparado: mesas 1-10 en sal\u00f3n. Flex 11-20 en reserva.';
  private atCapacity = false;
  private seq = 0;

  getState(): FloorState {
    return { tables: this.tables.map((t) => ({ ...t, session: t.session ? { ...t.session } : null })), modules: this.modules.map((m) => ({ ...m })), message: this.message, atCapacity: this.atCapacity };
  }

  getLayout(): FloorLayoutModule[] {
    const placed = this.modules.filter((m) => m.placed && m.sector !== 'storage');
    const byTable = new Map<number, PhysicalModule[]>();
    for (const m of placed) {
      const list = byTable.get(m.logicalTableId) ?? [];
      list.push(m);
      byTable.set(m.logicalTableId, list);
    }
    const anchors: PhysicalModule[] = [];
    for (const mods of byTable.values()) {
      const anchor = mods.reduce((best, m) =>
        m.gridCols * m.gridRows > best.gridCols * best.gridRows ? m : best,
      mods[0]);
      const guests = this.tables.find((t) => t.id === anchor.logicalTableId)?.session?.guests;
      anchors.push({
        ...anchor,
        seatCapacity: guests ?? mods.reduce((s, m) => s + m.seatCapacity, 0),
      });
    }
    return anchors.map((m) => {
      const w = gridToWorld(m.sector as 'salon' | 'patio', m.gridCol, m.gridRow, m.gridCols, m.gridRows);
      return { ...m, worldX: w.worldX, worldZ: w.worldZ };
    });
  }

  private sessionId(): string {
    this.seq += 1;
    return `sess-${this.seq}`;
  }

  seatAtTable(logicalTableId: number, guests: number): { ok: boolean; error?: string } {
    const table = this.tables.find((t) => t.id === logicalTableId);
    if (!table) return { ok: false, error: 'mesa_inexistente' };
    if (table.session) return { ok: false, error: 'mesa_ocupada' };
    if (guests > table.maxSeats) return { ok: false, error: 'excede_capacidad_mesa' };

    if (table.flexible) {
      const placed = this.modules.some((m) => m.logicalTableId === logicalTableId && m.placed);
      if (!placed) {
        const sector = table.sector === 'storage' ? 'patio' : (table.sector as 'salon' | 'patio');
        const composed = this.composeFlexibleTable(logicalTableId, guests, sector);
        if (!composed.ok) return composed;
      }
    }

    const sid = this.sessionId();
    table.session = { sessionId: sid, logicalTableId, guests, status: 'open' };
    for (const mod of this.modules.filter((m) => m.logicalTableId === logicalTableId && m.placed)) {
      mod.occupied = true;
      mod.sessionId = sid;
    }
    this.message = `Mesa ${logicalTableId}: ${guests} comensales.`;
    this.atCapacity = false;
    return { ok: true };
  }

  seatWalkIn(guests: number, prefer: FloorSector = 'salon'): { ok: boolean; tableId?: number; error?: string } {
    const free = this.tables.filter((t) => !t.session && t.sector === prefer && guests <= t.maxSeats);
    const fit = free.find((t) => guests <= t.baseSeats + 2) ?? free[0];
    if (!fit) {
      const alt = this.tables.filter((t) => !t.session && guests <= t.maxSeats);
      const pick = alt[0];
      if (!pick) return { ok: false, error: 'sin_mesa' };
      const r = this.seatAtTable(pick.id, guests);
      return r.ok ? { ok: true, tableId: pick.id } : { ok: false, error: r.error };
    }
    const r = this.seatAtTable(fit.id, guests);
    return r.ok ? { ok: true, tableId: fit.id } : { ok: false, error: r.error };
  }

  configureTable17For30(): { ok: boolean; error?: string } {
    return this.composeFlexibleTable(17, 30, 'patio');
  }

  composeFlexibleTable(logicalTableId: number, guests: number, sector: 'salon' | 'patio'): { ok: boolean; error?: string } {
    const table = this.tables.find((t) => t.id === logicalTableId);
    if (!table || !table.flexible) return { ok: false, error: 'mesa_no_flexible' };
    if (table.session) return { ok: false, error: 'mesa_ocupada' };

    const footprint = footprintForGuests(guests);
    const slot = findSlot(sector, footprint, this.modules);
    if (!slot) {
      this.atCapacity = true;
      this.message = 'Sin espacio: capacidad m\u00e1xima del sector alcanzada.';
      return { ok: false, error: 'sin_espacio' };
    }

    const needed = Math.max(2, Math.ceil(guests / 4));
    const pool = this.modules
      .filter((m) => m.kind === 'modulo' && !m.placed)
      .sort((a, b) => a.logicalTableId - b.logicalTableId || a.moduleId.localeCompare(b.moduleId));
    if (pool.length < needed) {
      this.atCapacity = true;
      this.message = 'Sin espacio: no hay m\u00f3dulos en reserva suficientes.';
      return { ok: false, error: 'sin_modulos' };
    }

    const chosen = pool.slice(0, needed);
    chosen.forEach((mod, i) => {
      mod.logicalTableId = logicalTableId;
      mod.placed = true;
      mod.sector = sector;
      mod.gridCol = slot.col + (i % footprint.cols);
      mod.gridRow = slot.row + Math.floor(i / footprint.cols);
      mod.gridCols = 1;
      mod.gridRows = 1;
      if (i === 0) {
        mod.gridCols = footprint.cols;
        mod.gridRows = footprint.rows;
      }
      mod.occupied = false;
      mod.sessionId = null;
    });

    table.sector = sector;
    this.message = `Mesa ${logicalTableId} preparada para hasta ${guests} en ${sector}.`;
    this.atCapacity = false;
    return { ok: true };
  }

  closeSession(logicalTableId: number): void {
    const table = this.tables.find((t) => t.id === logicalTableId);
    if (!table?.session) return;
    const sid = table.session.sessionId;
    table.session = null;
    for (const mod of this.modules.filter((m) => m.sessionId === sid)) {
      mod.occupied = false;
      mod.sessionId = null;
      if (table.flexible) {
        mod.placed = false;
        mod.sector = 'storage';
        const origin = /^m-(\d+)-/.exec(mod.moduleId);
        if (origin) mod.logicalTableId = Number(origin[1]);
      }
    }
    this.message = `Mesa ${logicalTableId} liberada.`;
  }

  simulateHighOccupancy(): void {
    for (let id = 1; id <= 8; id += 1) {
      if (!this.tables.find((t) => t.id === id)?.session) {
        this.seatAtTable(id, id <= 5 ? 2 : 6);
      }
    }
    this.message = 'Alta ocupaci\u00f3n simulada (mesas 1-8).';
  }

  reset(): void {
    this.tables = createLogicalTables();
    this.modules = createModules();
    this.message = 'Piso reiniciado.';
    this.atCapacity = false;
    this.seq = 0;
  }

  exportModules(): PhysicalModule[] {
    return this.modules.map((m) => ({ ...m }));
  }

  applyModules(modules: PhysicalModule[]): void {
    this.modules = modules.map((m) => ({ ...m }));
    this.message = 'Layout restaurado desde editor.';
  }

  resetLayoutToDefault(): void {
    this.modules = createModules();
    this.message = 'Distribuci\u00f3n predeterminada de mesas.';
  }

  getSalonObstacles(): { col: number; row: number; cols: number; rows: number }[] {
    return this.modules
      .filter((m) => m.placed && m.sector === 'salon')
      .map((m) => ({ col: m.gridCol, row: m.gridRow, cols: m.gridCols, rows: m.gridRows }));
  }

  snapModuleToWorld(moduleId: string, worldX: number, worldZ: number): { worldX: number; worldZ: number } | null {
    const mod = this.modules.find((m) => m.moduleId === moduleId && m.placed);
    if (!mod || mod.sector === 'storage') return null;
    const sector = mod.sector as GridSector;
    const { col, row } = worldToGridCell(sector, worldX, worldZ, mod.gridCols, mod.gridRows);
    if (!cellInBounds(sector, col, row, mod.gridCols, mod.gridRows)) return null;
    const w = gridCellToWorld(sector, col, row, mod.gridCols, mod.gridRows);
    return w;
  }

  relocateModule(moduleId: string, worldX: number, worldZ: number): { ok: boolean; error?: string } {
    const mod = this.modules.find((m) => m.moduleId === moduleId);
    if (!mod || !mod.placed) return { ok: false, error: 'modulo_no_colocado' };
    if (mod.sector === 'storage') return { ok: false, error: 'modulo_en_reserva' };
    if (mod.occupied) return { ok: false, error: 'mesa_ocupada' };
    const sector = mod.sector as GridSector;
    const { col, row } = worldToGridCell(sector, worldX, worldZ, mod.gridCols, mod.gridRows);
    if (!cellInBounds(sector, col, row, mod.gridCols, mod.gridRows)) {
      return { ok: false, error: 'fuera_de_grilla' };
    }
    const candidate = { col, row, cols: mod.gridCols, rows: mod.gridRows };
    const hit = this.modules.some(
      (m) =>
        m.placed &&
        m.sector === mod.sector &&
        m.moduleId !== moduleId &&
        collides(candidate, { col: m.gridCol, row: m.gridRow, cols: m.gridCols, rows: m.gridRows }),
    );
    if (hit) return { ok: false, error: 'colision' };
    mod.gridCol = col;
    mod.gridRow = row;
    this.message = `Mesa ${mod.logicalTableId} reubicada en grilla (${col},${row}).`;
    return { ok: true };
  }
}
